import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { PaymentService } from "../../services/paymentService";
import iconLogo from "../../assets/icon-logo.png";

export const PaymentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // State for teams and payment info
  const [teams, setTeams] = useState<any[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>("");
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  // Payment form state
  const [promotionCode, setPromotionCode] = useState("");
  const [codeValidated, setCodeValidated] = useState(false);
  const [codeValid, setCodeValid] = useState(false);
  const [promotionData, setPromotionData] = useState<any>(null);
  const [validationMessage, setValidationMessage] = useState("");
  const [requestInvoice, setRequestInvoice] = useState(false);
  const [splitInvoice, setSplitInvoice] = useState(false);

  // Payment monitoring state
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [currentPaymentId, setCurrentPaymentId] = useState<number | null>(null);

  // Existing payment state
  const [existingPayment, setExistingPayment] = useState<any>(null);
  const [showExistingPaymentOverlay, setShowExistingPaymentOverlay] =
    useState(false);

  const [invoice1Data, setInvoice1Data] = useState({
    company_name: "",
    company_address: "",
    tax_code: "",
    email: "",
  });

  const [invoice2Data, setInvoice2Data] = useState({
    company_name: "",
    company_address: "",
    tax_code: "",
    email: "",
  });

  // Separate error states for each invoice column
  const [invoice1Errors, setInvoice1Errors] = useState<string[]>([]);
  const [invoice2Errors, setInvoice2Errors] = useState<string[]>([]);

  // Validation function for Invoice 1
  const validateInvoice1 = (): string[] => {
    const errors: string[] = [];

    if (requestInvoice) {
      if (!invoice1Data.company_name.trim()) {
        errors.push("Company name is required");
      }
      if (!invoice1Data.company_address.trim()) {
        errors.push("Company address is required");
      }
      if (!invoice1Data.tax_code.trim()) {
        errors.push("Tax code is required");
      }
      if (!invoice1Data.email.trim()) {
        errors.push("Email is required");
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoice1Data.email)) {
        errors.push("Please enter a valid email address");
      }
    }

    return errors;
  };

  // Validation function for Invoice 2
  const validateInvoice2 = (): string[] => {
    const errors: string[] = [];

    if (requestInvoice && splitInvoice) {
      if (!invoice2Data.company_name.trim()) {
        errors.push("Company name is required");
      }
      if (!invoice2Data.company_address.trim()) {
        errors.push("Company address is required");
      }
      if (!invoice2Data.tax_code.trim()) {
        errors.push("Tax code is required");
      }
      if (!invoice2Data.email.trim()) {
        errors.push("Email is required");
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoice2Data.email)) {
        errors.push("Please enter a valid email address");
      }
    }

    return errors;
  };

  // Real-time validation effect for invoice data
  useEffect(() => {
    // Clear invoice-related errors when user is actively filling out invoice forms
    if (requestInvoice) {
      // Update individual column errors in real-time
      setInvoice1Errors(validateInvoice1());
      setInvoice2Errors(validateInvoice2());

      // Clear main error if it contains invoice errors
      if (error.includes("Invoice")) {
        setError("");
      }
    } else {
      // Clear all invoice errors when request invoice is disabled
      setInvoice1Errors([]);
      setInvoice2Errors([]);
    }
  }, [invoice1Data, invoice2Data, requestInvoice, splitInvoice]);

  // Helper function to extract specific error messages from API responses
  const getSpecificErrorMessage = (response: any): string => {
    // If there are validation errors, extract the first specific error
    if (response.errors && typeof response.errors === "object") {
      for (const field in response.errors) {
        if (
          response.errors[field] &&
          Array.isArray(response.errors[field]) &&
          response.errors[field].length > 0
        ) {
          return response.errors[field][0]; // Return the first error message
        }
      }
    }

    // Return the response message or fallback to generic
    return response.message || "An error occurred";
  };

  // Load user teams on component mount
  useEffect(() => {
    loadUserTeams();
  }, []);

  // Handle pre-selected team from URL parameters
  useEffect(() => {
    const teamFromUrl = searchParams.get("team");
    if (teamFromUrl && teams.length > 0) {
      const teamExists = teams.find((t) => t.team_id === teamFromUrl);
      if (teamExists) {
        setSelectedTeam(teamFromUrl);
      }
    }
  }, [searchParams, teams]);

  // Load payment info when team is selected
  useEffect(() => {
    if (selectedTeam) {
      // Clear previous state immediately when team changes
      setPaymentInfo(null);
      setError("");
      setExistingPayment(null);
      setShowExistingPaymentOverlay(false);

      const team = teams.find((t) => t.team_id === selectedTeam);
      if (team) {
        loadPaymentInfo(team.id);
      }
    } else {
      // Clear all payment-related state when no team is selected
      setPaymentInfo(null);
      setError("");
      setExistingPayment(null);
      setShowExistingPaymentOverlay(false);
    }
  }, [selectedTeam, teams]);

  const loadUserTeams = async () => {
    try {
      setLoading(true);
      const response = await PaymentService.getUserTeams();
      if (response.success && response.data) {
        setTeams(response.data);
        if (response.data.length > 0) {
          setSelectedTeam(response.data[0].team_id);
        }
      } else {
        setError(getSpecificErrorMessage(response));
      }
    } catch (err: any) {
      setError(err.message || "Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentInfo = async (teamId: number) => {
    try {
      const response = await PaymentService.getPaymentInfo(teamId);
      if (response.success && response.data) {
        setPaymentInfo(response.data);
        setError("");
        setExistingPayment(null);
        setShowExistingPaymentOverlay(false);
      } else {
        console.log("ABC", response);
        // Check if this is a completed payment scenario
        if (
          response.error === "PAYMENT_ALREADY_COMPLETED" &&
          response.team_info
        ) {
          // Clear all error states for completed payments
          setError("");
          setExistingPayment({
            status: "completed", // Set status as completed for successful payments
            reference: `${response.team_info.team_name} - ${response.team_info.payment_status}`,
            message: response.message,
          });
          setShowExistingPaymentOverlay(true);
          setPaymentInfo(null);
        } else if (response.payment_status && response.payment_reference) {
          // Check if there's an existing payment with different status
          setError("");
          setExistingPayment({
            status: response.payment_status,
            reference: response.payment_reference,
            message: response.message,
          });
          setShowExistingPaymentOverlay(true);
          setPaymentInfo(null);
        } else {
          setPaymentInfo(null);
          setExistingPayment(null);
          setShowExistingPaymentOverlay(false);
          setError(getSpecificErrorMessage(response));
        }
      }
    } catch (err) {
      setPaymentInfo(null);
      setExistingPayment(null);
      setShowExistingPaymentOverlay(false);
      setError("Failed to load payment information");
    }
  };

  const validateCode = async () => {
    if (!promotionCode.trim()) {
      setValidationMessage("Please enter a promotion code");
      setCodeValidated(true);
      setCodeValid(false);
      setPromotionData(null);
      return;
    }

    const team = teams.find((t) => t.team_id === selectedTeam);
    if (!team) return;

    try {
      const response = await PaymentService.validatePromotionCode(
        promotionCode,
        team.id
      );
      setCodeValidated(true);

      if (response.success && response.data) {
        setCodeValid(true);
        setPromotionData(response.data);
        setValidationMessage("");
      } else {
        setCodeValid(false);
        setPromotionData(null);
        setValidationMessage(getSpecificErrorMessage(response));
      }
    } catch (err: any) {
      setCodeValidated(true);
      setCodeValid(false);
      setPromotionData(null);
      setValidationMessage(err.message || "Failed to validate promotion code");
    }
  };

  const handlePromotionCodeChange = (value: string) => {
    setPromotionCode(value);
    // Reset validation state when user types
    if (codeValidated) {
      setCodeValidated(false);
      setCodeValid(false);
      setPromotionData(null);
      setValidationMessage("");
    }
  };

  // Combined validation function for payment processing
  const validateInvoiceData = (): { isValid: boolean; errors: string[] } => {
    const invoice1ValidationErrors = validateInvoice1();
    const invoice2ValidationErrors = validateInvoice2();

    // Update individual error states for display in columns
    setInvoice1Errors(invoice1ValidationErrors);
    setInvoice2Errors(invoice2ValidationErrors);

    // Combine all errors for main validation
    const allErrors = [
      ...invoice1ValidationErrors.map((err) => `Invoice 1: ${err}`),
      ...invoice2ValidationErrors.map((err) => `Invoice 2: ${err}`),
    ];

    return {
      isValid: allErrors.length === 0,
      errors: allErrors,
    };
  };

  const handlePayment = async () => {
    const team = teams.find((t) => t.team_id === selectedTeam);
    if (!team || !paymentInfo) return;

    // Validate invoice data before processing payment
    const validation = validateInvoiceData();
    if (!validation.isValid) {
      setError(validation.errors.join("\n"));
      return;
    }

    // Clear any previous errors if validation passes
    setError("");

    try {
      setPaymentInProgress(true);
      setPaymentStatus("Initiating payment...");

      // Get user's basic info from team data (use first member or fallback)
      const member = team.members?.[0];
      if (!member) {
        setError("Team member information is required for payment");
        setPaymentInProgress(false);
        return;
      }

      // Calculate payment amounts
      const registrationFee = paymentInfo.payment_info.amount;
      const discountAmount = promotionData
        ? (registrationFee * promotionData.discount_percentage) / 100
        : 0;
      const finalAmount = registrationFee - discountAmount;

      const paymentData = {
        team_id: team.id,
        amount: finalAmount, // Send final amount after discount
        registration_fee: registrationFee,
        discount_amount: discountAmount,
        promotion_code: promotionData?.code || null,
        buyer_first_name: member.full_name.split(" ")[0] || "Member",
        buyer_last_name:
          member.full_name.split(" ").slice(1).join(" ") || "Name",
        buyer_phone: member.personal_email.includes("@")
          ? "0123456789"
          : member.personal_email, // Fallback phone
        buyer_email: member.personal_email,
        buyer_address: "Ho Chi Minh City",
        buyer_city: "Ho Chi Minh City",
        buyer_state: "Ho Chi Minh",
        buyer_country: "vn",
        // Invoice information
        request_invoice: requestInvoice,
        split_invoice: splitInvoice,
        invoice1_data: requestInvoice ? invoice1Data : null,
        invoice2_data: requestInvoice && splitInvoice ? invoice2Data : null,
      };

      // Use VNPT Pay for real payment processing
      await PaymentService.processVNPTPayment(
        paymentData,
        (status: string) => {
          // Update status during processing
          const statusTexts: { [key: string]: string } = {
            creating: "Creating payment...",
            redirecting: "Opening payment gateway...",
            processing: "Processing payment...",
            checking: "Verifying payment...",
            completed: "Payment completed!",
            failed: "Payment failed",
            timeout: "Payment timeout",
            cancelled: "Payment cancelled",
          };
          setPaymentStatus(statusTexts[status] || `Payment status: ${status}`);
        },
        (success: boolean, orderCode?: string) => {
          // Handle completion
          setPaymentInProgress(false);
          if (success) {
            setPaymentStatus("Payment completed successfully!");
            // Refresh payment info to show completed status
            setTimeout(() => {
              loadPaymentInfo(team.id);
              setPaymentStatus("");
              setCurrentPaymentId(null);
            }, 3000);
          } else {
            setPaymentStatus("Payment was not completed. Please try again.");
            setTimeout(() => {
              setPaymentStatus("");
              setCurrentPaymentId(null);
            }, 5000);
          }
        }
      );
    } catch (err: any) {
      setPaymentInProgress(false);
      setPaymentStatus("");
      alert(err.message || "Failed to process payment");
    }
  };

  // Get payment status message for existing payments
  const getPaymentStatusMessage = (status: string) => {
    switch (status) {
      case "completed":
        return {
          title: "Payment Successful",
          message:
            "This team has already completed payment successfully. Please check your email.",
          color: "green",
          icon: "✅",
          showRetry: false,
        };
      case "processing":
      case "pending":
        return {
          title: "Payment Pending Approval",
          message:
            "Payment was made but not approved by the payment gateway system.",
          color: "yellow",
          icon: "⏳",
          showRetry: false,
        };
      case "failed":
      case "timeout":
        return {
          title: "Payment Unsuccessful",
          message:
            "Payment was unsuccessful due to exceeding the specified time without receiving transaction confirmation. Please contact the hotline for support.",
          color: "red",
          icon: "❌",
          showRetry: true,
          hotlineSupport: true,
        };
      default:
        return {
          title: "Payment Status Unknown",
          message: "There is an existing payment with unknown status.",
          color: "gray",
          icon: "❓",
          showRetry: false,
        };
    }
  };

  // Calculate payment amounts
  const getPaymentAmounts = () => {
    if (!paymentInfo)
      return { registrationFee: 0, discountAmount: 0, finalAmount: 0 };

    const registrationFee = paymentInfo.payment_info.amount;
    const discountAmount = promotionData
      ? (registrationFee * promotionData.discount_percentage) / 100
      : 0;
    const finalAmount = registrationFee - discountAmount;

    return { registrationFee, discountAmount, finalAmount };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment information...</p>
        </div>
      </div>
    );
  }

  const { registrationFee, discountAmount, finalAmount } = getPaymentAmounts();

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {/* Payment Monitoring Overlay */}
      {paymentInProgress && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Processing Payment
              </h3>
              <p className="text-gray-600 mb-4">
                Please wait a moment to check your transaction
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-sm text-blue-800">{paymentStatus}</p>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Do not close this window. We are monitoring your payment status.
              </p>
              {currentPaymentId && (
                <p className="text-xs text-gray-400 mt-2">
                  Payment ID: {currentPaymentId}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back Button and Header */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate("/dashboard/payment")}
            className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Teams
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Payment</h1>
        </div>

        {/* Payment Notice */}
        {paymentInfo && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              {paymentInfo.payment_info.description}
            </p>
          </div>
        )}

        {/* Existing Payment Notice */}
        {existingPayment &&
          !paymentInfo &&
          existingPayment.status != "completed" && (
            <div
              className={`mb-6 border rounded-lg p-4 ${
                getPaymentStatusMessage(existingPayment.status).color ===
                "green"
                  ? "bg-green-50 border-green-200"
                  : getPaymentStatusMessage(existingPayment.status).color ===
                    "yellow"
                  ? "bg-yellow-50 border-yellow-200"
                  : "bg-red-50 border-red-200"
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className="text-2xl">
                  {getPaymentStatusMessage(existingPayment.status).icon}
                </div>
                <div className="flex-1">
                  <h4
                    className={`font-medium ${
                      getPaymentStatusMessage(existingPayment.status).color ===
                      "green"
                        ? "text-green-800"
                        : getPaymentStatusMessage(existingPayment.status)
                            .color === "yellow"
                        ? "text-yellow-800"
                        : "text-red-800"
                    }`}
                  >
                    {getPaymentStatusMessage(existingPayment.status).title}
                  </h4>
                  <p
                    className={`text-sm mt-1 ${
                      getPaymentStatusMessage(existingPayment.status).color ===
                      "green"
                        ? "text-green-700"
                        : getPaymentStatusMessage(existingPayment.status)
                            .color === "yellow"
                        ? "text-yellow-700"
                        : "text-red-700"
                    }`}
                  >
                    {getPaymentStatusMessage(existingPayment.status).message}
                  </p>
                  <p className="text-xs text-gray-600 mt-2">
                    Reference: {existingPayment.reference}
                  </p>
                  {getPaymentStatusMessage(existingPayment.status)
                    .hotlineSupport && (
                    <div className="mt-2 text-xs text-blue-600">
                      Support Hotline: 1900-XXXX (24/7)
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch relative">
          {/* Overlay for Invoice Columns Only - handles both existing payments and errors */}
          {(showExistingPaymentOverlay && existingPayment) ||
          (error && !paymentInfo && !existingPayment) ? (
            <div className="absolute inset-0 z-20 lg:left-1/3 lg:ml-3 bg-black bg-opacity-30 rounded-lg flex items-center justify-center backdrop-blur-sm">
              <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-4">
                <div className="text-center">
                  {existingPayment ? (
                    // Existing payment overlay
                    <>
                      <div className="text-4xl mb-4 text-center">
                        {existingPayment.status == "completed" ? (
                          <img src={iconLogo} className="w-28 mx-auto" />
                        ) : (
                          getPaymentStatusMessage(existingPayment.status).icon
                        )}
                      </div>
                      <h3
                        className={`text-xl font-bold mb-3 ${
                          getPaymentStatusMessage(existingPayment.status)
                            .color === "green"
                            ? "text-green-800"
                            : getPaymentStatusMessage(existingPayment.status)
                                .color === "yellow"
                            ? "text-yellow-800"
                            : getPaymentStatusMessage(existingPayment.status)
                                .color === "red"
                            ? "text-red-800"
                            : "text-gray-800"
                        }`}
                      >
                        {getPaymentStatusMessage(existingPayment.status).title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {
                          getPaymentStatusMessage(existingPayment.status)
                            .message
                        }
                      </p>
                      <div
                        className={`text-sm px-4 py-2 rounded-full inline-block ${
                          getPaymentStatusMessage(existingPayment.status)
                            .color === "green"
                            ? "bg-green-100 text-green-800"
                            : getPaymentStatusMessage(existingPayment.status)
                                .color === "yellow"
                            ? "bg-yellow-100 text-yellow-800"
                            : getPaymentStatusMessage(existingPayment.status)
                                .color === "red"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        Reference: {existingPayment.reference}
                      </div>

                      {/* Show hotline support info for timeout/failed payments */}
                      {getPaymentStatusMessage(existingPayment.status)
                        .hotlineSupport && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-800 font-medium">
                            Support Contact:
                          </p>
                          <p className="text-sm text-blue-700">
                            Hotline: 1900-XXXX
                          </p>
                          <p className="text-xs text-blue-600">
                            Available 24/7 for payment support
                          </p>
                        </div>
                      )}

                      {/* Show retry button only when appropriate */}
                      {getPaymentStatusMessage(existingPayment.status)
                        .showRetry && (
                        <button
                          onClick={() => {
                            setShowExistingPaymentOverlay(false);
                            setExistingPayment(null);
                          }}
                          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                        >
                          Try Payment Again
                        </button>
                      )}
                    </>
                  ) : (
                    // Error message overlay
                    <>
                      <div className="w-20 mx-auto flex items-center justify-center mb-4">
                        <img src={iconLogo} className="w-full" />
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-red-800">
                        Payment Not Available
                      </h3>
                      <div className="text-gray-600 mb-4">
                        {error.split("\n").map((errorLine, index) => (
                          <p key={index} className="mb-1">
                            {errorLine}
                          </p>
                        ))}
                      </div>
                      {/* <button
                        onClick={loadUserTeams}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        Retry
                      </button> */}
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* Payment Options Column */}
          <div
            className={`flex ${
              paymentInProgress ? "pointer-events-none opacity-60" : ""
            }`}
          >
            <div className="bg-white rounded-lg shadow-sm border p-6 w-full flex flex-col">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Payment Options
              </h2>

              {/* Team Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                  Paying for team
                </label>
                <select
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {teams.map((team) => (
                    <option key={team.id} value={team.team_id}>
                      {team.team_id} - {team.team_name}
                    </option>
                  ))}
                </select>
                {/* Show team info and payment eligibility */}
              </div>
              {/* Promotion Code - Only show if payment is allowed */}
              {paymentInfo && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Referral code
                  </label>
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                    <input
                      type="text"
                      value={promotionCode}
                      onChange={(e) =>
                        handlePromotionCodeChange(e.target.value)
                      }
                      className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter code"
                    />
                    <button
                      onClick={validateCode}
                      className="flex-shrink-0 px-4 py-2 bg-gray-800 text-white text-sm rounded-md hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      Check
                    </button>
                  </div>
                  {codeValidated && codeValid && promotionData && (
                    <p className="text-xs text-green-600 mt-1">
                      {promotionData.description} (-
                      {promotionData.discount_percentage}%)
                    </p>
                  )}
                  {codeValidated && !codeValid && validationMessage && (
                    <p className="text-xs text-red-600 mt-1">
                      {validationMessage}
                    </p>
                  )}
                </div>
              )}

              {/* Summary - Only show if payment is allowed */}
              {paymentInfo && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">
                    Summary
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Registration Fee</span>
                      <span className="text-gray-900">
                        {new Intl.NumberFormat("vi-VN").format(registrationFee)}
                      </span>
                    </div>
                    {discountAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Discount</span>
                        <span className="text-gray-900">
                          -
                          {new Intl.NumberFormat("vi-VN").format(
                            discountAmount
                          )}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between font-medium pt-2 border-t border-gray-200">
                      <span className="text-gray-900">Pay today</span>
                      <span className="text-gray-900">
                        {new Intl.NumberFormat("vi-VN").format(finalAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Invoice Options - Only show if payment is allowed */}
              {paymentInfo && (
                <div className="mb-6">
                  <p className="text-xs text-gray-600 mb-4 text-left">
                    We will issue the electronic VAT invoice on your request.
                    Please fill out the information required.
                    <a
                      target="_blank"
                      href="https://vietnamyounglions.net/terms-and-conditions/"
                      className="text-blue-600 underline cursor-pointer"
                    >
                      Terms & Conditions
                    </a>{" "}
                    applied.
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <label className="flex items-center cursor-pointer">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={requestInvoice}
                            onChange={(e) =>
                              setRequestInvoice(e.target.checked)
                            }
                            className="sr-only"
                          />
                          <div
                            className={`w-12 h-6 rounded-full transition-colors ${
                              requestInvoice ? "bg-green-500" : "bg-gray-300"
                            }`}
                          >
                            <div
                              className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                                requestInvoice
                                  ? "translate-x-6"
                                  : "translate-x-0.5"
                              } translate-y-0.5`}
                            ></div>
                          </div>
                        </div>
                        <span className="ml-3 text-sm text-gray-900">
                          Request Invoice
                        </span>
                      </label>
                    </div>

                    <div className="flex items-center">
                      <label className="flex items-center cursor-pointer">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={splitInvoice}
                            onChange={(e) => setSplitInvoice(e.target.checked)}
                            disabled={!requestInvoice}
                            className="sr-only"
                          />
                          <div
                            className={`w-12 h-6 rounded-full transition-colors ${
                              splitInvoice && requestInvoice
                                ? "bg-green-500"
                                : "bg-gray-300"
                            }`}
                          >
                            <div
                              className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                                splitInvoice && requestInvoice
                                  ? "translate-x-6"
                                  : "translate-x-0.5"
                              } translate-y-0.5`}
                            ></div>
                          </div>
                        </div>
                        <span
                          className={`ml-3 text-sm ${
                            !requestInvoice ? "text-gray-400" : "text-gray-900"
                          }`}
                        >
                          Split Invoice
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Validation Error Display */}
              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-left">
                  <div className="flex items-start">
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Please complete all required fields
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        {error.split("\n").map((errorLine, index) => (
                          <p key={index} className="mb-1">
                            {errorLine}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Pay Now Button - Only show if payment is allowed */}
              {paymentInfo && (
                <div className="mt-auto">
                  <button
                    onClick={handlePayment}
                    disabled={
                      !paymentInfo ||
                      paymentInProgress ||
                      (requestInvoice && invoice1Errors.length > 0) ||
                      (requestInvoice &&
                        splitInvoice &&
                        invoice2Errors.length > 0)
                    }
                    className="w-full py-3 bg-gray-800 text-white font-medium rounded-md hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {paymentInProgress ? "Processing..." : "Pay now"}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Invoice 1 Information */}
          <div
            className={`flex ${
              paymentInProgress ? "pointer-events-none opacity-60" : ""
            }`}
          >
            <div
              className={`rounded-lg shadow-sm border p-6 w-full flex flex-col ${
                requestInvoice ? "bg-white" : "bg-gray-100"
              }`}
            >
              <h2
                className={`text-lg font-semibold mb-4 ${
                  requestInvoice ? "text-gray-900" : "text-gray-400"
                }`}
              >
                Invoice 1 Information
              </h2>

              <div className="space-y-4 flex-grow">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 text-left ${
                      requestInvoice ? "text-gray-700" : "text-gray-400"
                    }`}
                  >
                    Company name
                  </label>
                  <input
                    type="text"
                    value={invoice1Data.company_name}
                    onChange={(e) =>
                      setInvoice1Data({
                        ...invoice1Data,
                        company_name: e.target.value,
                      })
                    }
                    disabled={!requestInvoice}
                    className={`w-full px-3 py-2 border rounded-md ${
                      requestInvoice
                        ? "border-gray-300 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        : "border-gray-200 bg-gray-100 text-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 text-left ${
                      requestInvoice ? "text-gray-700" : "text-gray-400"
                    }`}
                  >
                    Company address
                  </label>
                  <input
                    type="text"
                    value={invoice1Data.company_address}
                    onChange={(e) =>
                      setInvoice1Data({
                        ...invoice1Data,
                        company_address: e.target.value,
                      })
                    }
                    disabled={!requestInvoice}
                    className={`w-full px-3 py-2 border rounded-md ${
                      requestInvoice
                        ? "border-gray-300 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        : "border-gray-200 bg-gray-100 text-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 text-left ${
                      requestInvoice ? "text-gray-700" : "text-gray-400"
                    }`}
                  >
                    Tax code
                  </label>
                  <input
                    type="text"
                    value={invoice1Data.tax_code}
                    onChange={(e) =>
                      setInvoice1Data({
                        ...invoice1Data,
                        tax_code: e.target.value,
                      })
                    }
                    disabled={!requestInvoice}
                    className={`w-full px-3 py-2 border rounded-md ${
                      requestInvoice
                        ? "border-gray-300 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        : "border-gray-200 bg-gray-100 text-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 text-left ${
                      requestInvoice ? "text-gray-700" : "text-gray-400"
                    }`}
                  >
                    Email to receive invoice
                  </label>
                  <input
                    type="email"
                    value={invoice1Data.email}
                    onChange={(e) =>
                      setInvoice1Data({
                        ...invoice1Data,
                        email: e.target.value,
                      })
                    }
                    disabled={!requestInvoice}
                    className={`w-full px-3 py-2 border rounded-md ${
                      requestInvoice
                        ? "border-gray-300 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        : "border-gray-200 bg-gray-100 text-gray-400"
                    }`}
                  />
                </div>
              </div>
              {/* Invoice 1 Error Display */}
              {requestInvoice && invoice1Errors.length > 0 && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-left">
                  <div className="flex items-start">
                    <div className="ml-2">
                      <div className="text-xs text-red-700">
                        {invoice1Errors.map((error, index) => (
                          <p key={index} className="mb-1">
                            {error}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Invoice 2 Information */}
          <div
            className={`flex ${
              paymentInProgress ? "pointer-events-none opacity-60" : ""
            }`}
          >
            <div
              className={`rounded-lg shadow-sm border p-6 w-full flex flex-col ${
                requestInvoice && splitInvoice ? "bg-white" : "bg-gray-100"
              }`}
            >
              <h2
                className={`text-lg font-semibold mb-4 ${
                  requestInvoice && splitInvoice
                    ? "text-gray-900"
                    : "text-gray-400"
                }`}
              >
                Invoice 2 Information
              </h2>

              <div className="space-y-4 flex-grow">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 text-left ${
                      requestInvoice && splitInvoice
                        ? "text-gray-700"
                        : "text-gray-400"
                    }`}
                  >
                    Company name
                  </label>
                  <input
                    type="text"
                    value={invoice2Data.company_name}
                    onChange={(e) =>
                      setInvoice2Data({
                        ...invoice2Data,
                        company_name: e.target.value,
                      })
                    }
                    disabled={!requestInvoice || !splitInvoice}
                    className={`w-full px-3 py-2 border rounded-md ${
                      requestInvoice && splitInvoice
                        ? "border-gray-300 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        : "border-gray-200 bg-gray-100 text-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 text-left ${
                      requestInvoice && splitInvoice
                        ? "text-gray-700"
                        : "text-gray-400"
                    }`}
                  >
                    Company address
                  </label>
                  <input
                    type="text"
                    value={invoice2Data.company_address}
                    onChange={(e) =>
                      setInvoice2Data({
                        ...invoice2Data,
                        company_address: e.target.value,
                      })
                    }
                    disabled={!requestInvoice || !splitInvoice}
                    className={`w-full px-3 py-2 border rounded-md ${
                      requestInvoice && splitInvoice
                        ? "border-gray-300 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        : "border-gray-200 bg-gray-100 text-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 text-left ${
                      requestInvoice && splitInvoice
                        ? "text-gray-700"
                        : "text-gray-400"
                    }`}
                  >
                    Tax code
                  </label>
                  <input
                    type="text"
                    value={invoice2Data.tax_code}
                    onChange={(e) =>
                      setInvoice2Data({
                        ...invoice2Data,
                        tax_code: e.target.value,
                      })
                    }
                    disabled={!requestInvoice || !splitInvoice}
                    className={`w-full px-3 py-2 border rounded-md ${
                      requestInvoice && splitInvoice
                        ? "border-gray-300 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        : "border-gray-200 bg-gray-100 text-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 text-left ${
                      requestInvoice && splitInvoice
                        ? "text-gray-700"
                        : "text-gray-400"
                    }`}
                  >
                    Email to receive invoice
                  </label>
                  <input
                    type="email"
                    value={invoice2Data.email}
                    onChange={(e) =>
                      setInvoice2Data({
                        ...invoice2Data,
                        email: e.target.value,
                      })
                    }
                    disabled={!requestInvoice || !splitInvoice}
                    className={`w-full px-3 py-2 border rounded-md ${
                      requestInvoice && splitInvoice
                        ? "border-gray-300 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        : "border-gray-200 bg-gray-100 text-gray-400"
                    }`}
                  />
                </div>
              </div>
              {/* Invoice 2 Error Display */}
              {requestInvoice && splitInvoice && invoice2Errors.length > 0 && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-left">
                  <div className="flex items-start">
                    <div className="ml-2">
                      <div className="text-xs text-red-700">
                        {invoice2Errors.map((error, index) => (
                          <p key={index} className="mb-1">
                            {error}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
